import axios from 'axios';
import axiosInstance from './axios-instance';
import { resolveOwnerScopedAccountId } from '../helper/accountScope';

export const QUICKBOOKS_BUNDLE_SETUP_REQUIRED_EVENT = 'quipa:quickbooks-bundle-setup-required';

const pickQuickBooksErrorFields = (data: any = {}) => ({
  ...(data?.isQboError !== undefined ? { isQboError: data.isQboError } : {}),
  ...(data?.quickBooksError !== undefined ? { quickBooksError: data.quickBooksError } : {}),
  ...(data?.errorSource ? { errorSource: data.errorSource } : {}),
  ...(data?.errorTitle ? { errorTitle: data.errorTitle } : {}),
  ...(data?.qboFaultCode ? { qboFaultCode: data.qboFaultCode } : {}),
  ...(data?.qboFaultSummary ? { qboFaultSummary: data.qboFaultSummary } : {}),
});

const isQuickBooksBundleSetupResponse = (data: any = {}) => {
  const message = String(data?.message || '').toLowerCase();
  return (
    data?.code === 'QBO_BUNDLE_SETUP_REQUIRED' ||
    (message.includes('quickbooks bundle item is missing') &&
      message.includes('export without bundling'))
  );
};

const emitQuickBooksBundleSetupRequired = (data: any = {}) => {
  if (typeof window === 'undefined' || !isQuickBooksBundleSetupResponse(data)) return;
  window.dispatchEvent(
    new CustomEvent(QUICKBOOKS_BUNDLE_SETUP_REQUIRED_EVENT, {
      detail: data,
    })
  );
};

const quickbooksOauth = async (
  accountId: number,
  importClients: boolean,
  companyId?: string // NEW optional argument
) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const qs = new URLSearchParams({
      accountId: String(scopedAccountId),
      importClients: String(importClients),
    });

    if (companyId && companyId.trim()) {
      qs.set('companyId', companyId.trim());
    }

    const response = await axiosInstance.get(`/quickBooks/oauth?${qs.toString()}`, {
      withCredentials: true,
    });

    return response.data;
  } catch (error) {
    console.log('Error in oauth', error);
    throw error;
  }
};

const quickbooksCheckIsConnected = async (accountId: number) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const response = await axiosInstance.post(
      `/quickBooks/check-connection`,
      { accountId: scopedAccountId },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.log('Error in quickbooksCheckIsConnected', error);
    return { erorr: true, message: error };
  }
};

const importQBOClients = async (accountId: number, customersData: any, mappedCustomers: any) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const response = await axiosInstance.post(
      `/quickBooks/import-customers`,
      { accountId: scopedAccountId, customersData, mappedCustomers },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.log('Error in quickbooksCheckIsConnected', error);
    return { erorr: true, message: error };
  }
};

const getQuickBooksInfo = async (accountId: number) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const response = await axiosInstance.post(
      `/quickBooks/getInfo`,
      { accountId: scopedAccountId },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.log('Error in get user qb info', error);
  }
};

const fetchQBOCustomers = async (accountId: number) => {
  const scopedAccountId = resolveOwnerScopedAccountId(accountId);
  const qs = new URLSearchParams({
    accountId: String(scopedAccountId),
    cacheBust: String(Date.now()),
  });
  try {
    const response = await axiosInstance.get(`/quickBooks/qbo-customers?${qs.toString()}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    console.log('Error in fetchQBOCustomers: ', error);
    throw error;
  }
};

const revoke = async (accountId: number) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const response = await axiosInstance.post(
      `/quickBooks/revoke`,
      { accountId: scopedAccountId },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.log('Error in revoke', error);
  }
};

const exportEstimate = async (
  ids: number[],
  accountId: number,
  fees: number[],
  forceNoBundling = false
) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const response = await axiosInstance.post(
      `/quickBooks/export`,
      { draperyOrderIds: ids, accountId: scopedAccountId, fees, forceNoBundling },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('Error in export estimate to quickbooks', error);
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data || {};
      const hasMessage =
        error.response && error.response.data && typeof error.response.data.message === 'string';

      const code = responseData?.code;
      const missingBundleItems = responseData?.missingBundleItems;

      const message = hasMessage
        ? responseData.message
        : error.message;

      return {
        error: true,
        message,
        code,
        missingBundleItems,
        ...pickQuickBooksErrorFields(responseData),
      };
    }
    if (error instanceof Error) {
      return { error: true, message: error.message };
    }
    return { error: true, message: 'An unknown error occurred' };
  }
};

const exportEstimateFromBuilder = async (payload: {
  accountId: number;
  estimateId: number;
  forceNoBundling?: boolean;
  estimateTerms?: {
    paymentTerms?: string;
    termsAndConditions?: string;
    noteToCustomer?: string;
  };
}) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(payload?.accountId);
    const response = await axiosInstance.post(
      `/quickBooks/export-estimate`,
      {
        ...payload,
        accountId: scopedAccountId,
      },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('Error in export estimate (builder) to quickbooks', error);
    if (axios.isAxiosError(error)) {
      const responseData = error.response?.data || {};
      const hasMessage =
        error.response && error.response.data && typeof error.response.data.message === 'string';
      const code = responseData?.code;
      const missingBundleItems = responseData?.missingBundleItems;
      const message = hasMessage
        ? responseData.message
        : error.message;
      emitQuickBooksBundleSetupRequired(responseData);
      return {
        error: true,
        message,
        code,
        missingBundleItems,
        ...pickQuickBooksErrorFields(responseData),
      };
    }
    if (error instanceof Error) {
      return { error: true, message: error.message };
    }
    return { error: true, message: 'An unknown error occurred' };
  }
};

const exportFeeEstimate = async (ids: number[], accountId: number) => {
  try {
    const scopedAccountId = resolveOwnerScopedAccountId(accountId);
    const response = await axiosInstance.post(
      `/quickBooks/export-fee`,
      { feeIds: ids, accountId: scopedAccountId },
      { withCredentials: true }
    );
    return response.data;
  } catch (error) {
    console.error('Error in export fee estimate to quickbooks', error);
    if (axios.isAxiosError(error)) {
      const hasMessage =
        error.response && error.response.data && typeof error.response.data.message === 'string';

      const message = hasMessage
        ? error.response && error.response.data && error.response.data.message
        : error.message;

      return { error: true, message };
    }
    if (error instanceof Error) {
      return { error: true, message: error.message };
    }
    return { error: true, message: 'An unknown error occurred' };
  }
};

export {
  quickbooksOauth,
  quickbooksCheckIsConnected,
  revoke,
  exportEstimate,
  exportEstimateFromBuilder,
  getQuickBooksInfo,
  exportFeeEstimate,
  fetchQBOCustomers,
  importQBOClients,
};
